/**
 * DiffHopUpload.jsx
 *
 * Input page for the DiffHop scaffold-hopping workflow.
 * Responsibilities:
 *  - Target structure: PDB ID lookup (via docking service) OR file upload OR example
 *  - Chain selector: parse chains from loaded PDB, let user remove unwanted chains
 *  - Ligand source: auto (co-crystallised) OR user SDF upload
 *  - nMolecules: 1–500
 *  - Submit → diffhopService.generateScaffolds → QueueNotificationModal
 *
 * Props:
 *   userId, userName, projectId, projectName  — injected by caller from MSAL/location state
 *   onSubmitSuccess {fn(taskId)}              — optional callback after queue
 */

import React, { useRef, useState, useEffect, useCallback,useContext, } from 'react';
import {
  Upload, Dna, FlaskConical, Loader2,
  AlertCircle, CheckCircle2, X, Beaker, Hash,
} from 'lucide-react';

import ChainSelector from './ChainSelector';
import QueueNotificationModal from '../../retro/QueueNotificationModal';
import {
  resolvePdbId,
  filterPdbChains,
  parsePdbChains,
  generateScaffolds,
} from './diffhopService';
import { useMsal } from '@azure/msal-react';
import { UserContext } from '../../UserContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Loader from '../../Loader';


// ─── Molstar ──────────────────────────────────────────────────────────────────
const MOLSTAR_ORIGIN = 'https://cascade-454406.web.app';
const MOLSTAR_URL    = `${MOLSTAR_ORIGIN}/viewer/index.html?v=20260424-4`;

if (typeof document !== 'undefined' && !document.getElementById('difhop-spin-kf')) {
  const st = document.createElement('style');
  st.id = 'difhop-spin-kf';
  st.textContent = `@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`;
  document.head.appendChild(st);
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = {
  root: {
    display: 'flex',
    gap: 16,
    padding: 16,
    height: 'calc(100vh - 80px)',   // 64px navbar (pt-16) + 16px margin (mt-4)
    overflow: 'hidden',
    fontFamily: "'Inter', system-ui, sans-serif",
    boxSizing: 'border-box',
    background: '#F4F4F4',
  },
  // ── LEFT ──
  previewCard: {
    flex: '1 1 0',
    minWidth: 0,
    background: '#fff',
    borderRadius: 16,
    border: '1px solid #ede9f8',
    display: 'flex',
    flexDirection: 'column',
    padding: 16,
    gap: 10,
    overflow: 'hidden',
  },
  previewHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
  previewTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: '#1a1a2e',
    letterSpacing: '-0.01em',
  },
  molstarBadge: {
    fontSize: 12,
    fontWeight: 500,
    color: '#7c5cbf',
    background: '#ede9f8',
    borderRadius: 8,
    padding: '3px 10px',
  },
  viewerWrap: {
    flex: 1,
    position: 'relative',
    minHeight: 0,
    borderRadius: 10,
    background: '#faf9fe',
    border: '1px solid #ede9f8',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iframeLayer: {
    position: 'absolute',
    inset: 0,
    transition: 'opacity 0.3s ease',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
    padding: '0 32px',
    textAlign: 'center',
  },
  emptyText: {
    fontSize: 13,
    color: '#a89dcf',
    margin: 0,
    lineHeight: 1.5,
  },
  centeredState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
    padding: '0 32px',
    textAlign: 'center',
  },
  stateText: {
    fontSize: 13,
    color: '#9d84d8',
    margin: 0,
  },
  errorText: {
    fontSize: 13,
    color: '#e05252',
    margin: 0,
  },
  spinner: { animation: 'spin 1s linear infinite' },
  infoBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    background: '#f5f0ff',
    border: '1px solid #d9cff5',
    borderRadius: 8,
    padding: '8px 12px',
    fontSize: 12,
    color: '#5a3fa0',
    lineHeight: 1.5,
    flexShrink: 0,
  },
  // ── RIGHT ──
  controlsCol: {
    width: 316,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    overflowY: 'auto',
    paddingRight: 2,
  },
  card: {
    background: '#fff',
    borderRadius: 14,
    border: '1px solid #ede9f8',
    padding: '14px 14px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    flexShrink: 0,
  },
  cardHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cardLabel: {
    margin: 0,
    fontSize: 13,
    fontWeight: 700,
    color: '#1a1a2e',
    letterSpacing: '-0.01em',
  },
  exampleBtn: {
    fontSize: 11,
    fontWeight: 600,
    color: '#7c5cbf',
    background: '#f0ecfa',
    border: '1px solid #d9cff5',
    borderRadius: 8,
    padding: '3px 9px',
    cursor: 'pointer',
    letterSpacing: '0.01em',
  },
  tabRow: {
    display: 'flex',
    gap: 8,
  },
  tabBtn: {
    flex: 1,
    padding: '8px 10px',
    borderRadius: 10,
    border: '1.5px solid',
    fontSize: 13,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    background: 'none',
    outline: 'none',
    textAlign: 'center',
  },
  inputWrap: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  textInput: {
    width: '100%',
    padding: '10px 36px 10px 12px',
    borderRadius: 10,
    border: '1.5px solid #e2ddf0',
    fontSize: 14,
    color: '#1a1a2e',
    background: '#fafafa',
    outline: 'none',
    transition: 'border-color 0.15s',
    boxSizing: 'border-box',
    fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  numberInput: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 10,
    border: '1.5px solid #e2ddf0',
    fontSize: 14,
    color: '#1a1a2e',
    background: '#fafafa',
    outline: 'none',
    transition: 'border-color 0.15s',
    boxSizing: 'border-box',
  },
  inputIcon: {
    position: 'absolute',
    right: 12,
    top: 11,
  },
  errorMsg: {
    fontSize: 11,
    color: '#e05252',
    margin: 0,
    paddingLeft: 2,
  },
  inputHint: {
    fontSize: 11,
    color: '#999',
    margin: 0,
    paddingLeft: 2,
  },
  uploadArea: {
    border: '1.5px dashed #d0c8ee',
    borderRadius: 10,
    padding: '12px 14px',
    cursor: 'pointer',
    background: '#faf9fe',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  uploadPrompt: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  uploadText: { fontSize: 12, color: '#888' },
  uploadExt: {
    fontFamily: "'JetBrains Mono', monospace",
    color: '#7c5cbf',
    fontWeight: 600,
    fontSize: 11,
  },
  fileRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    width: '100%',
  },
  fileName: {
    fontSize: 12,
    color: '#444',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  clearBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#aaa',
    padding: 2,
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  toggleRow: {
    display: 'flex',
    gap: 8,
  },
  toggleBtn: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '9px 10px',
    borderRadius: 10,
    border: '1.5px solid',
    fontSize: 13,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    background: 'none',
    outline: 'none',
  },
  divider: {
    border: 'none',
    borderTop: '1px solid #f0ecfa',
    margin: '2px 0',
  },
  submitBtn: {
    width: '100%',
    padding: '13px 0',
    borderRadius: 12,
    border: 'none',
    background: 'linear-gradient(135deg, #7c5cbf 0%, #5a3fa0 100%)',
    color: '#fff',
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: '-0.01em',
    cursor: 'pointer',
    transition: 'opacity 0.2s ease',
    boxShadow: '0 2px 12px rgba(124,92,191,0.25)',
    flexShrink: 0,
  },
  submitHint: {
    margin: 0,
    fontSize: 11,
    color: '#a89dcf',
    textAlign: 'center',
    flexShrink: 0,
  },
  taskIdBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: 8,
    padding: '7px 10px',
    fontSize: 11,
    color: '#166534',
    fontFamily: "'JetBrains Mono', monospace",
    flexShrink: 0,
  },
};

// ─── Sub-components ───────────────────────────────────────────────────────────
function TabBtn({ active, onClick, label }) {
  return (
    <button
      style={{
        ...styles.tabBtn,
        background:   active ? '#ede9f8' : '#fafafa',
        borderColor:  active ? '#c4b5e8' : '#e2ddf0',
        color:        active ? '#5a3fa0' : '#666',
        fontWeight:   active ? 600 : 400,
      }}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function ToggleBtn({ active, onClick, icon, label }) {
  return (
    <button
      style={{
        ...styles.toggleBtn,
        background:  active ? '#ede9f8' : '#fafafa',
        borderColor: active ? '#c4b5e8' : '#e2ddf0',
        color:       active ? '#5a3fa0' : '#666',
        fontWeight:  active ? 600 : 400,
      }}
      onClick={onClick}
    >
      {icon}
      {label}
    </button>
  );
}

// ─── MolstarViewer ────────────────────────────────────────────────────────────
const MolstarViewer = React.forwardRef(function MolstarViewer({ structure }, ref) {
  const iframeRef = useRef(null);
  const loadedRef = useRef(false);
  const queueRef  = useRef([]);

  function postToViewer(msg) {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    try { win.postMessage(msg, MOLSTAR_ORIGIN); } catch { /* noop */ }
  }

  function sendMsg(msg) {
    if (!loadedRef.current) { queueRef.current.push(msg); return; }
    postToViewer(msg);
  }

  React.useImperativeHandle(ref, () => ({ send: sendMsg }));

  useEffect(() => {
    loadedRef.current = false;
    queueRef.current  = [];
    function onMessage(event) {
      if (event.origin !== MOLSTAR_ORIGIN) return;
      if (!iframeRef.current?.contentWindow) return;
      if (event.data?.type !== 'MOLSTAR_READY') return;
      loadedRef.current = true;
      queueRef.current.forEach(postToViewer);
      queueRef.current = [];
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    if (!structure?.text) return;
    const fmt = structure.format || 'pdb';
    sendMsg({ type: 'LOAD_STRUCTURE', data: structure.text, format: fmt });
    if (fmt === 'pdb' || fmt === 'pdbqt')
      sendMsg({ type: 'LOAD_COMPLEX', structure: structure.text, format: fmt });
  }, [structure]);

  return (
    <iframe
      key={MOLSTAR_URL}
      ref={iframeRef}
      src={MOLSTAR_URL}
      title="Molecular Viewer"
      style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
      allow="fullscreen"
      allowFullScreen
      sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads"
    />
  );
});

// ─── Main component ───────────────────────────────────────────────────────────
export default function DiffHopUpload({
  
  onSubmitSuccess,
}) {
  // ── Target structure ──────────────────────────────────────────────────────
  const [targetTab, setTargetTab]   = useState('id');   // 'id' | 'file'
  const [pdbId, setPdbId]           = useState('');
  const [pdbFile, setPdbFile]       = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
   const { projectName, projectDescription, id } = location.state || {};
   const projectId = id ;
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();
    const userName = activeAccount?.name;
    const { userProfile } = useContext(UserContext);
    const userId = userProfile?.userId;
    
  // Raw PDB text from either source, BEFORE chain filtering
  const [rawPdbText, setRawPdbText] = useState(null);
  // PDB text AFTER chain filtering — sent to viewer and API
  const [pdbText, setPdbText]       = useState(null);
  // The File object that the API will receive (rebuilt from filtered text when chains change)
  const [activePdbFile, setActivePdbFile] = useState(null);

  const [viewerState, setViewerState] = useState('empty'); // empty|loading|ready|error
  const [pdbError, setPdbError]       = useState('');
  // { text, format } fed to MolstarViewer
  const [structure, setStructure]     = useState(null);

  // Resolved PDB ID (from backend proxy response — canonical form)
  const [resolvedPdbId, setResolvedPdbId] = useState('');

  // ── Chain management ──────────────────────────────────────────────────────
  const [availableChains, setAvailableChains] = useState([]);
  const [selectedChains, setSelectedChains]   = useState([]);
  // Approval banner: shown when chains differ from original set
  const [chainsModified, setChainsModified]   = useState(false);

  // ── Ligand source ─────────────────────────────────────────────────────────
  const [ligandSource, setLigandSource] = useState('auto'); // 'auto' | 'upload'
  const [sdfFile, setSdfFile]           = useState(null);

  // ── nMolecules ────────────────────────────────────────────────────────────
  const [nMolecules, setNMolecules] = useState(10);

  // ── Submission ────────────────────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError]   = useState('');
  const [queueResult, setQueueResult]   = useState(null); // { taskId, status, chains_used }
  const [showLoader, setShowLoader]     = useState(false);

  // ── Refs ──────────────────────────────────────────────────────────────────
  const molstarRef  = useRef(null);
  const debounceRef = useRef(null);
  const sdfInputRef = useRef(null);
  const pdbInputRef = useRef(null);

  // ─── Internal helpers ──────────────────────────────────────────────────────

  /** Called whenever we have fresh PDB text (from ID or file). Sets all derived state. */
  function applyRawPdb(text, canonicalId = '') {
    setRawPdbText(text);
    const chains = parsePdbChains(text);
    setAvailableChains(chains);
    setSelectedChains(chains);          // all chains selected by default
    setChainsModified(false);
    setResolvedPdbId(canonicalId);
    commitPdbText(text, text, canonicalId); // initial: filtered === raw
  }

  /**
   * Rebuilds the viewer structure and active file from the current filtered text.
   * @param {string} filtered  - PDB text after chain removal
   * @param {string} raw       - Original PDB text (used for filename)
   * @param {string} id        - PDB ID for the filename
   */
  function commitPdbText(filtered, raw, id) {
    setPdbText(filtered);
    setStructure({ text: filtered, format: 'pdb' });
    const name = id ? `${id}.pdb` : (pdbFile?.name ?? 'structure.pdb');
    const file = new File([filtered], name, { type: 'chemical/x-pdb' });
    setActivePdbFile(file);
  }

  /** Re-apply chain filter whenever the selection changes (after initial load). */
  function applyChainFilter(nextChains) {
    if (!rawPdbText) return;
    const allChains = availableChains;
    const noneRemoved = nextChains.length === allChains.length;
    setChainsModified(!noneRemoved);
    const filtered = noneRemoved
      ? rawPdbText
      : filterPdbChains(rawPdbText, nextChains);
    commitPdbText(filtered, rawPdbText, resolvedPdbId);
  }

  function resetTarget() {
    setRawPdbText(null);
    setPdbText(null);
    setStructure(null);
    setActivePdbFile(null);
    setAvailableChains([]);
    setSelectedChains([]);
    setChainsModified(false);
    setResolvedPdbId('');
    setViewerState('empty');
    setPdbError('');
  }

  function resetAll() {
    // target structure
    resetTarget();
    setPdbId('');
    setPdbFile(null);
    setTargetTab('id');
    if (pdbInputRef.current) pdbInputRef.current.value = '';

    // ligand
    setLigandSource('auto');
    setSdfFile(null);
    if (sdfInputRef.current) sdfInputRef.current.value = '';

    // molecules
    setNMolecules(10);

    // submission
    setQueueResult(null);
    setShowLoader(false);
    setSubmitError('');
  }

  // ─── PDB ID tab ────────────────────────────────────────────────────────────

  const loadPdbById = useCallback(async (id) => {
    if (!id || id.length < 4) {
      resetTarget();
      return;
    }
    setViewerState('loading');
    setPdbError('');
    try {
      const { pdbId: canonical, pdbText: text } = await resolvePdbId(id);
      applyRawPdb(text, canonical);
      setViewerState('ready');
    } catch (err) {
      setPdbError(err.message);
      setViewerState('error');
      setRawPdbText(null);
      setStructure(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handlePdbIdChange(e) {
    const val = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 4);
    setPdbId(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => loadPdbById(val), 400);
  }

  // ─── PDB file tab ──────────────────────────────────────────────────────────

  async function handlePdbFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setPdbFile(file);
    setViewerState('loading');
    setPdbError('');
    try {
      const text = await file.text();
      applyRawPdb(text, '');
      setViewerState('ready');
    } catch {
      setPdbError('Could not read PDB file.');
      setViewerState('error');
    }
  }

  async function handleLoadExample() {
    setViewerState('loading');
    setPdbError('');
    try {
      const res = await fetch('/1QCF.pdb');
      if (!res.ok) throw new Error('Example file not found in /public.');
      const text = await res.text();
      const exFile = new File([text], '1QCF.pdb', { type: 'chemical/x-pdb' });
      setPdbFile(exFile);
      applyRawPdb(text, '1QCF');
      setViewerState('ready');
    } catch (err) {
      setPdbError(err.message);
      setViewerState('error');
    }
  }

  // ─── Tab switching ─────────────────────────────────────────────────────────

  function handleTargetTabChange(tab) {
    setTargetTab(tab);
    resetTarget();
    if (tab === 'id') {
      setPdbFile(null);
      if (pdbInputRef.current) pdbInputRef.current.value = '';
    } else {
      setPdbId('');
    }
  }

  // ─── Ligand source ─────────────────────────────────────────────────────────

  function handleLigandSourceChange(val) {
    setLigandSource(val);
    if (val === 'auto') {
      setSdfFile(null);
      if (sdfInputRef.current) sdfInputRef.current.value = '';
    }
  }

  // ─── Chain selection change ────────────────────────────────────────────────

  function handleChainChange(nextChains) {
    setSelectedChains(nextChains);
    applyChainFilter(nextChains);
  }

  // ─── nMolecules ───────────────────────────────────────────────────────────

  function handleNMoleculesChange(e) {
    const val = Math.min(500, Math.max(1, parseInt(e.target.value, 10) || 1));
    setNMolecules(val);
  }

 

  const canSubmit =
    viewerState === 'ready' &&
    !isSubmitting &&
    (ligandSource === 'auto' || (ligandSource === 'upload' && sdfFile)) &&
    nMolecules >= 1 &&
    nMolecules <= 500;

  const submitHint =
    viewerState !== 'ready'
      ? 'Load a target structure to continue'
      : ligandSource === 'upload' && !sdfFile
      ? 'Upload a ligand SDF to continue'
      : '';

       // ─── Submit ────────────────────────────────────────────────────────────────

 async function handleSubmit() {
  if (!canSubmit) return;
  setIsSubmitting(true);
  setSubmitError('');
  setShowLoader(true);
  try {
    const result = await generateScaffolds({
      ligandSource:  ligandSource === 'auto' ? 'auto_ligand' : 'upload_ligand',
      pdbId:         targetTab === 'id' ? resolvedPdbId || pdbId : undefined,
      nMolecules,
      userId,
      userName,
      projectId,
      projectName,
      proteinFile:   targetTab === 'file' ? activePdbFile : undefined,
      ligandFile:    ligandSource === 'upload' ? sdfFile : undefined,
    });
    setShowLoader(true);       // show loader immediately after API responds
    setQueueResult(result);    // queue modal mounts behind it
    onSubmitSuccess?.(result.taskId);
  } catch (err) {
    setShowLoader(false); 
    setSubmitError(err.message || 'Submission failed. Please try again.');
  } finally {
    setIsSubmitting(false);
  }
}

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <>
    <div className="text-[13px] text-[#9690b0] flex-1">
  <Link to="/dashboard" className="hover:text-[#1e1b2e] hover:underline">
    Home
  </Link>

  <span className="mx-0.5">&rsaquo;</span>

  <Link to="/generativeai" className="hover:text-[#1e1b2e] hover:underline">
    Projects
  </Link>

  <span className="mx-0.5">&rsaquo;</span>

  <button
    type="button"
    onClick={() => navigate(-2)}
    className="hover:text-[#1e1b2e] hover:underline"
  >
    Project Details
  </button>

  <span className="mx-0.5">&rsaquo;</span>
   <button
    type="button"
    onClick={() => navigate(-1)}
    className="hover:text-[#1e1b2e] hover:underline"
  >
    Flow Selection
  </button>

  <span className="mx-0.5">&rsaquo;</span>

  <b className="text-[#1e1b2e] font-bold">
     Diffhop Scaffold Hopping
  </b>
</div>
      <div style={styles.root}>
 
        {/* ── LEFT: Structure preview ──────────────────────────────────── */}
        <div style={styles.previewCard}>
          <div style={styles.previewHeader}>
            <span style={styles.previewTitle}>Structure preview</span>
            <span style={styles.molstarBadge}>Molstar</span>
          </div>

          <div style={styles.viewerWrap}>
            {viewerState === 'empty' && (
              <div style={styles.emptyState}>
                <Dna size={36} color="#c4b5e8" strokeWidth={1.4} />
                <p style={styles.emptyText}>
                  Provide a PDB ID or upload a file to preview the target structure
                </p>
              </div>
            )}

            {viewerState === 'loading' && (
              <div style={styles.centeredState}>
                <Loader2 size={28} color="#9d84d8" strokeWidth={1.8} style={styles.spinner} />
                <p style={styles.stateText}>Fetching structure…</p>
              </div>
            )}

            {viewerState === 'error' && (
              <div style={styles.centeredState}>
                <AlertCircle size={30} color="#e05252" strokeWidth={1.6} />
                <p style={styles.errorText}>{pdbError || 'Could not load structure'}</p>
              </div>
            )}

            {/* Iframe always mounted — opacity reveals it */}
            <div
              style={{
                ...styles.iframeLayer,
                opacity:       viewerState === 'ready' ? 1 : 0,
                pointerEvents: viewerState === 'ready' ? 'auto' : 'none',
              }}
            >
              <MolstarViewer ref={molstarRef} structure={structure} />
            </div>
          </div>

          {/* Auto-ligand info banner */}
          {viewerState === 'ready' && ligandSource === 'auto' && (
            <div style={styles.infoBanner}>
              <CheckCircle2
                size={13}
                color="#7c5cbf"
                strokeWidth={2}
                style={{ flexShrink: 0, marginTop: 1 }}
              />
              <span>
                Pocket will be auto-detected. Any co-crystallised heteroatom ligand in the
                structure is shown as the inferred pocket reference — not your scaffold.
              </span>
            </div>
          )}

          {/* Chains-modified approval banner */}
          {viewerState === 'ready' && chainsModified && (
            <div
              style={{
                ...styles.infoBanner,
                background: '#fffbeb',
                border: '1px solid #f5d87a',
                color: '#7a5a00',
              }}
            >
              <AlertCircle
                size={13}
                color="#b07d00"
                strokeWidth={2}
                style={{ flexShrink: 0, marginTop: 1 }}
              />
              <span>
                The viewer and the API will use the <strong>filtered structure</strong> with
                only chain{selectedChains.length > 1 ? 's' : ''}{' '}
                <strong>{selectedChains.join(', ')}</strong>. The original file is unchanged
                locally — only the submitted PDB is filtered.
              </span>
            </div>
          )}
        </div>

        {/* ── RIGHT: Controls ──────────────────────────────────────────── */}
        <div style={styles.controlsCol}>

          {/* ── Target structure card ── */}
          <div style={styles.card}>
            <div style={styles.cardHeaderRow}>
              <p style={styles.cardLabel}>Target structure</p>
              {/* Example button only visible on file tab */}
              {targetTab === 'file' && (
                <button style={styles.exampleBtn} onClick={handleLoadExample}>
                  Get example
                </button>
              )}
            </div>

            <div style={styles.tabRow}>
              <TabBtn
                active={targetTab === 'id'}
                onClick={() => handleTargetTabChange('id')}
                label="PDB ID"
              />
              <TabBtn
                active={targetTab === 'file'}
                onClick={() => handleTargetTabChange('file')}
                label="Upload PDB"
              />
            </div>

            {/* ── PDB ID input ── */}
            {targetTab === 'id' && (
              <div style={styles.inputWrap}>
                <input
                  style={{
                    ...styles.textInput,
                    borderColor:
                      viewerState === 'error'
                        ? '#e05252'
                        : viewerState === 'ready'
                        ? '#b9a9e4'
                        : '#e2ddf0',
                  }}
                  type="text"
                  placeholder="e.g. 4HJO"
                  value={pdbId}
                  onChange={handlePdbIdChange}
                  maxLength={4}
                  spellCheck={false}
                  autoComplete="off"
                />
                {viewerState === 'loading' && (
                  <Loader2
                    size={16}
                    color="#9d84d8"
                    style={{ ...styles.inputIcon, ...styles.spinner }}
                  />
                )}
                {viewerState === 'ready' && pdbId && (
                  <CheckCircle2 size={16} color="#7c5cbf" style={styles.inputIcon} />
                )}
                {viewerState === 'error' && (
                  <AlertCircle size={16} color="#e05252" style={styles.inputIcon} />
                )}
                {viewerState === 'error' && (
                  <p style={styles.errorMsg}>{pdbError}</p>
                )}
                {viewerState === 'ready' && resolvedPdbId && (
                  <p style={styles.inputHint}>
                    Resolved: <strong>{resolvedPdbId}</strong> — structure loaded from RCSB
                  </p>
                )}
              </div>
            )}

            {/* ── PDB file upload ── */}
            {targetTab === 'file' && (
              <div style={styles.uploadArea} onClick={() => pdbInputRef.current?.click()}>
                <input
                  ref={pdbInputRef}
                  type="file"
                  accept=".pdb,.pdbqt,.ent"
                  style={{ display: 'none' }}
                  onChange={handlePdbFileChange}
                />
                {pdbFile ? (
                  <div style={styles.fileRow}>
                    <CheckCircle2 size={14} color="#7c5cbf" />
                    <span style={styles.fileName}>{pdbFile.name}</span>
                    <button
                      style={styles.clearBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        setPdbFile(null);
                        resetTarget();
                        if (pdbInputRef.current) pdbInputRef.current.value = '';
                      }}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <div style={styles.uploadPrompt}>
                    <Upload size={16} color="#9d84d8" strokeWidth={1.6} />
                    <span style={styles.uploadText}>
                      Click to upload{' '}
                      <span style={styles.uploadExt}>.pdb / .pdbqt</span>
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* ── Chain selector — shown once structure is loaded ── */}
            {viewerState === 'ready' && availableChains.length > 0 && (
              <>
                <hr style={styles.divider} />
                <ChainSelector
                  availableChains={availableChains}
                  selectedChains={selectedChains}
                  onChange={handleChainChange}
                  disabled={viewerState === 'loading'}
                />
              </>
            )}
          </div>

          {/* ── Ligand source card ── */}
          <div style={styles.card}>
            <p style={styles.cardLabel}>Ligand source</p>
            <div style={styles.toggleRow}>
              <ToggleBtn
                active={ligandSource === 'auto'}
                onClick={() => handleLigandSourceChange('auto')}
                icon={<FlaskConical size={15} strokeWidth={1.8} />}
                label="Auto ligand"
              />
              <ToggleBtn
                active={ligandSource === 'upload'}
                onClick={() => handleLigandSourceChange('upload')}
                icon={<Upload size={15} strokeWidth={1.8} />}
                label="Upload SDF"
              />
            </div>

            {ligandSource === 'upload' && (
              <div
                style={styles.uploadArea}
                onClick={() => sdfInputRef.current?.click()}
              >
                <input
                  ref={sdfInputRef}
                  type="file"
                  accept=".sdf,.mol"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) setSdfFile(file);
                  }}
                />
                {sdfFile ? (
                  <div style={styles.fileRow}>
                    <CheckCircle2 size={14} color="#7c5cbf" />
                    <span style={styles.fileName}>{sdfFile.name}</span>
                    <button
                      style={styles.clearBtn}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        setSdfFile(null);
                        if (sdfInputRef.current) sdfInputRef.current.value = '';
                      }}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <div style={styles.uploadPrompt}>
                    <Upload size={16} color="#9d84d8" strokeWidth={1.6} />
                    <span style={styles.uploadText}>
                      Click to upload{' '}
                      <span style={styles.uploadExt}>.sdf / .mol</span>
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Molecules to generate card ── */}
          <div style={styles.card}>
            <div style={styles.cardHeaderRow}>
              <p style={styles.cardLabel}>Molecules to generate</p>
              {/* <Hash size={13} color="#a89dcf" strokeWidth={2} /> */}
            </div>
            <div style={styles.inputWrap}>
              <input
                style={{
                  ...styles.numberInput,
                  borderColor:
                    nMolecules < 1 || nMolecules > 500 ? '#e05252' : '#e2ddf0',
                }}
                type="number"
                min={1}
                max={500}
                value={nMolecules}
                onChange={handleNMoleculesChange}
              />
              <p style={styles.inputHint}>Maximum 500 molecules per job</p>
            </div>
          </div>

          {/* ── Submit error ── */}
          {submitError && (
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 7,
                background: '#fff5f5',
                border: '1px solid #fecaca',
                borderRadius: 8,
                padding: '8px 10px',
                fontSize: 12,
                color: '#b91c1c',
                flexShrink: 0,
              }}
            >
              <AlertCircle size={13} color="#b91c1c" strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{submitError}</span>
            </div>
          )}

          {/* Spacer */}
          <div style={{ flex: 1 }} />

          {/* ── Submit button ── */}
          <button
            style={{
              ...styles.submitBtn,
              opacity: canSubmit ? 1 : 0.48,
              cursor:  canSubmit ? 'pointer' : 'not-allowed',
            }}
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
          Run DiffHop
          </button>

          {submitHint && !isSubmitting && (
            <p style={styles.submitHint}>{submitHint}</p>
          )}
        </div>
      </div>

      {/* ── Queue notification modal ── */}
      {showLoader && (
        <div className="fixed inset-0 flex items-center justify-center backdrop-blur-sm bg-opacity-50 z-50">
          <Loader />
        </div>
      )}

      {queueResult && (
       <QueueNotificationModal
          taskId={queueResult.taskId}
          projectId={projectId}
          workflowType="generativeai"
          onClose={resetAll}
          onHistory={resetAll}
        />
      )}
    </>
  );
}